import { IImageLibraryDto } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { Component } from "react";
import { FilePicker } from "../../../../common/file-picker";
import { ResourceImage } from "../../../../common/resource-image";
import KuikaColorPicker from "../../../../ui-kit/kuika-color-picker/kuika-color-picker";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ConfigField } from "../config-pane-parts";
import "./app-settings-config-splash-inputs.scss";

/** A fixed pair, so it is a constant rather than a fetch. */
const SPLASH_FITTINGS: KuikaSelectboxOption[] = [
  { value: "default", label: "Default", testId: "splashDefaultSelection", searchLabel: "Default" },
  { value: "stretch", label: "Stretch", testId: "splashStretchSelection", searchLabel: "Stretch" }
];

interface ISplashInputProps {
  allImage?: IImageLibraryDto[];
  parentData: string;
  splashImage: string | null;
  splashBgColor: string;
  splashFitting: "default" | "stretch";
  handleGetImageLibrary: () => void;
  /**
   * Three independently bound fields, not one combined write: the image, the colour and the
   * fitting are three separate form paths now, and a combined setter would have to be handed
   * the other two's CURRENT value on every edit just to leave them alone.
   */
  onImageChange: (image: string | null) => void;
  onColorChange: (color: string) => void;
  onFittingChange: (fitting: "default" | "stretch") => void;
}
interface ISplashInputState {
  isSplashPopoverVisible: boolean;
}
class SplashInput extends Component<ISplashInputProps, ISplashInputState> {
  constructor(props) {
    super(props);
    this.state = {
      isSplashPopoverVisible: false
    };
  }

  // The library is fetched when the picker opens. It used to hang off a click handler on
  // the frame, and the picker — portaled by Radix but still a React child — bubbled every
  // click inside it back out to that handler, refetching on each one.
  handlePopoverVisibleChange = (visible: boolean): void => {
    this.setState({ isSplashPopoverVisible: visible });
    if (visible) this.props.handleGetImageLibrary();
  };

  handleChangeImage = (image: string | null = null) => {
    this.props.onImageChange(image);
  };

  handleChangeFitting = (fitting: "default" | "stretch") => {
    this.props.onFittingChange(fitting);
  };

  // The colour picker hands back the value itself, so there is no event to stop — the two
  // calls that used to be here belonged to the native colour input this replaces.
  handleChangeColor = (color: string): void => {
    this.props.onColorChange(color.replace(/#/i, ""));
  };

  render() {
    const image = this.props.allImage?.find((icon) => icon.fileName === this.props.splashImage);

    return (
      <>
        <div className="kuika-config-splash__preview">
          <div className="kuika-config-splash__frame">
            <svg
              className="kuika-config-splash__body"
              width="121"
              height="207"
              viewBox="0 0 121 207"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="-0.5"
                width="120"
                height="206"
                rx="11.5"
                transform="matrix(1 0 0 -1 0 206)"
                fill={`#${this.props.splashBgColor}`}
                stroke="var(--blue-3)"
                strokeLinecap="round"
              />
            </svg>

            <svg
              className="kuika-config-splash__notch"
              width="44"
              height="7"
              viewBox="0 0 44 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M44 0H0V1C1.10457 1 2 1.89543 2 3V5C2 6.10457 2.89543 7 4 7H40C41.1046 7 42 6.10457 42 5V3C42 1.89543 42.8954 1 44 1V0Z"
                fill="var(--blue-3)"
              />
            </svg>

            <KuikaPopover
              className="kuika-file-picker-popover"
              position="left"
              hideCloseIcon
              // The picker panel is wide and these live in the pane's narrow right column,
              // so without a collision gap Radix flushes it against the viewport edge.
              collisionPadding={16}
              content={
                <FilePicker
                  files={this.props.allImage || []}
                  acceptedFileTypes=".png"
                  fileSize={0.3}
                  getImageLibrary={this.props.handleGetImageLibrary}
                  onChange={(_value, image) => {
                    this.handleChangeImage(image.fileName);
                  }}
                  onDeselect={() => {
                    this.handleChangeImage(null);
                  }}
                  enableDeselect
                  showFileDragger
                  selectedId={image?.fileName}
                />
              }
              visible={this.state.isSplashPopoverVisible}
              onVisibleChange={this.handlePopoverVisibleChange}
            >
              {/* The trigger owns no open state of its own: Radix toggles it and composes
                  this child's handlers, so a second `onClick` here fought the first. */}
              <ResourceImage
                className={classNames("kuika-config-splash__image", {
                  "kuika-config-splash__image--stretch": this.props.splashFitting === "stretch"
                })}
                src={image?.content}
                fallbackSrc="/images/kuika-splash.svg"
                alt="Splash preview"
              />
            </KuikaPopover>
          </div>
        </div>

        {this.props.splashImage && (
          <div className="kuika-config-splash__file">
            <span className="kuika-config-splash__name" title={this.props.splashImage}>
              {this.props.splashImage}
            </span>
            <span
              className="kuika-config-choose"
              onClick={() => {
                this.handleChangeImage();
              }}
            >
              Remove
            </span>
          </div>
        )}

        {/* Explicit paths on Fitting and Color: both controls are driven through props, not a
            named `Form.Item` of their own, so the discovery walk has nothing to find. */}
        <ConfigField
          aside
          label="Fitting"
          formInputName={[this.props.parentData, "imageFit"]}
          tooltipContentKey={
            this.props.parentData === "androidConfig" ? "android_splash_fitting" : "ios_splash_fitting"
          }
        >
          <KuikaSelectbox
            size="md"
            data-testid="splashFittingDropdown"
            placeholder="Select a option"
            value={this.props.splashFitting}
            onChange={this.handleChangeFitting}
            options={SPLASH_FITTINGS}
          />
        </ConfigField>

        <ConfigField
          aside
          label="Color"
          formInputName={[this.props.parentData, "splashScreenBackcolor"]}
          tooltipContentKey={this.props.parentData === "androidConfig" ? "android_splash_color" : "ios_splash_color"}
        >
          <KuikaColorPicker
            dataTestId="splashColorField"
            color={this.props.splashBgColor ? `#${this.props.splashBgColor}` : "#ffffff"}
            onChange={this.handleChangeColor}
          />
        </ConfigField>
      </>
    );
  }
}

export { SplashInput };
